import {PaymentTypeEnum} from '@joyrideautos/auction-core/src/dtos/PaymentsDto';
import {PaymentIntentHoldDto} from '@joyrideautos/auction-core/src/dtos/PaymentIntentHoldDto';
import {
    GeneratePayInFullSellerReceiptReqData,
    GenerateDepositReceiptRPCReqData,
    GenerateReceiptRPCResData,
    GeneratePayInFullBuyerReceiptReqData,
} from '@joyrideautos/auction-core/src/types/requests/rpcReqTypes/payments/generateReceiptReqTypes';
import {
    StoreReceiptsRPCReqData,
    StoreReceiptsRPCResData,
} from '@joyrideautos/auction-core/src/types/requests/rpcReqTypes/payments/storeReceiptsReqTypes';
import {
    GeneratePDFBulkReceiptReqData,
    GeneratePDFBulkReceiptResData,
} from '@joyrideautos/auction-core/src/types/requests/rpcReqTypes/payments/GeneratePDFBulkReceiptReqTypes';
import {
    UpdatePaymentMethodRPCReqData,
    UpdatePaymentMethodRPCResData,
} from '@joyrideautos/auction-core/src/types/requests/rpcReqTypes/payments/UpdatePaymentMethodReqTypes';
import {
    AddPaymentMethodRPCReqData,
    AddPaymentMethodRPCResData,
} from '@joyrideautos/auction-core/src/types/requests/rpcReqTypes/payments/AddPaymentMethodReqTypes';
import {
    RemovePaymentMethodRPCReqData,
    RemovePaymentMethodRPCResData,
} from '@joyrideautos/auction-core/src/types/requests/rpcReqTypes/payments/RemovePaymentMethodReqTypes';
import {
    CreatePaymentMethodSetupIntentRPCReqData,
    CreatePaymentMethodSetupIntentRPCResData,
} from '@joyrideautos/auction-core/src/types/requests/rpcReqTypes/payments/CreatePaymentMethodSetupIntentReqTypes';
import {FeReqRouteEnum} from '@joyrideautos/auction-core/src/services/FERoutingService';
import {
    ConfirmCcHoldAndPlaceCcHoldForBidderRPCReqData,
    ConfirmCcHoldAndPlaceCcHoldForBidderRPCResData,
} from '@joyrideautos/auction-core/src/types/requests/rpcReqTypes/payments/confirmCcHoldAndPlaceCcHoldForBidderReqTypes';
import {
    CreateOrderRPCReqData,
    CreateOrderRPCResData,
} from '@joyrideautos/auction-core/src/types/requests/rpcReqTypes/orders/createOrderReqTypes';
import {
    CancelOrderRPCReqData,
    CancelOrderRPCResData,
} from '@joyrideautos/auction-core/src/types/requests/rpcReqTypes/orders/cancelOrderReqTypes';
import {
    CreateOrderPaymentIntentRPCReqData,
    CreateOrderPaymentIntentRPCResData,
} from '@joyrideautos/auction-core/src/types/requests/rpcReqTypes/orders/createOrderPaymentIntentReqTypes';
import {
    ApplyAdditionalOrderFeeRPCReqData,
    ApplyAdditionalOrderFeeRPCResData,
} from '@joyrideautos/auction-core/src/types/requests/rpcReqTypes/orders/applyAdditionalOrderFeeReqTypes';
import {
    GetOrderPaymentIntentDetailsRPCReqData,
    GetOrderPaymentIntentDetailsRPCResData,
} from '@joyrideautos/auction-core/src/types/requests/rpcReqTypes/orders/getOrderPaymentIntentDetailsReqTypes';
import {BaseService} from './BaseService';
import {promiseWithTimeout} from '@joyrideautos/auction-utils/src/PromiseUtils';
import {
    CONFIRM_CC_HOLD_PER_AUCTION_TIMEOUT_IN_MS,
    CONFIRM_CC_HOLD_PER_VEHICLE_TIMEOUT_IN_MS,
} from '@joyrideautos/auction-core/src/constants/Constants';
import {WithKey} from '@joyrideautos/auction-core/src/types/common';
import {CCHoldScopeEnum} from '@joyrideautos/auction-core/src/types/CCHoldScopes';

export class PaymentService extends BaseService {
    async addPaymentMethod(data: AddPaymentMethodRPCReqData): Promise<AddPaymentMethodRPCResData> {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_ADD_PAYMENT_METHOD)(data);
    }

    async removePaymentMethod(data: RemovePaymentMethodRPCReqData): Promise<RemovePaymentMethodRPCResData> {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_REMOVE_PAYMENT_METHOD)(data);
    }

    async updatePaymentMethod(data: UpdatePaymentMethodRPCReqData): Promise<UpdatePaymentMethodRPCResData> {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_UPDATE_PAYMENT_METHOD)(data);
    }

    async getFeesBreakdownForAmount(
        amount: number,
        regionId: string,
        auctionId: string,
        itemKey: string
    ): Promise<any> {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_GET_FEES_BREAKDOWN_FOR_AMOUNT)({
            amount,
            regionId,
            auctionId,
            itemKey,
        });
    }

    async generateDepositReceipt(data: GenerateDepositReceiptRPCReqData): Promise<GenerateReceiptRPCResData> {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_GENERATE_DEPOSIT_RECEIPT)(data);
    }

    async generatePayInFullBuyerReceipt(
        data: GeneratePayInFullBuyerReceiptReqData
    ): Promise<GenerateReceiptRPCResData> {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_GENERATE_PAY_IN_FULL_BUYER_RECEIPT)(data);
    }

    async generatePayInFullSellerReceipt(
        data: GeneratePayInFullSellerReceiptReqData
    ): Promise<GenerateReceiptRPCResData> {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_GENERATE_PAY_IN_FULL_SELLER_RECEIPT)(
            data
        );
    }

    async storePDFReceipts(data: StoreReceiptsRPCReqData): Promise<StoreReceiptsRPCResData> {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_STORE_PDF_RECEIPTS, {
            timeout: 540_000,
        })(data);
    }

    async generatePDFBulkReceipt(data: GeneratePDFBulkReceiptReqData): Promise<GeneratePDFBulkReceiptResData> {
        return this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_GENERATE_PDF_BULK_RECEIPT, {
            timeout: 540_000,
        })(data);
    }

    async createPaymentMethodSetupIntent(
        data: CreatePaymentMethodSetupIntentRPCReqData
    ): Promise<CreatePaymentMethodSetupIntentRPCResData> {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_CREATE_PAYMENT_METHOD_SETUP_INTENT)(data);
    }

    async createOrder(data: CreateOrderRPCReqData): Promise<CreateOrderRPCResData> {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_CREATE_ORDER)(data);
    }

    async cancelOrder(data: CancelOrderRPCReqData): Promise<CancelOrderRPCResData> {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_CANCEL_ORDER)(data);
    }

    async applyAdditionalOrderFee(data: ApplyAdditionalOrderFeeRPCReqData): Promise<ApplyAdditionalOrderFeeRPCResData> {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_APPLY_ADDITIONAL_ORDER_FEE)(data);
    }

    async createOrderPaymentIntent(
        data: CreateOrderPaymentIntentRPCReqData
    ): Promise<CreateOrderPaymentIntentRPCResData> {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_CREATE_ORDER_PAYMENT_INTENT)(data);
    }

    async getOrderPaymentIntentDetails(
        data: GetOrderPaymentIntentDetailsRPCReqData
    ): Promise<GetOrderPaymentIntentDetailsRPCResData> {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_GET_ORDER_PAYMENT_INTENT_DETAILS)(data);
    }

    // Note: this endpoint is not used. At the moment we use checkIfUserIsAbleToPayForItemsLocal instead
    async checkIfUserIsAbleToPayForItems(itemsIds: Array<string | number>, paymentType: PaymentTypeEnum): Promise<any> {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_CHECK_IF_USER_IS_ABLE_TO_PAY_FOR_ITEMS)({
            itemsIds,
            paymentType,
        });
    }

    async checkHasFailedDeposits(userId: string, itemIds: Array<string | number>) {
        const snapshot = await this.firebase.database.fetchOnceSnapshot(`/users/${userId}/failedDeposits`);
        if (!snapshot.exists()) {
            return false;
        }
        let hasFailedDeposits = false;
        snapshot.forEach((child) => {
            if (!child.exists()) {
                return;
            }
            hasFailedDeposits = hasFailedDeposits || itemIds.includes(child.key!);
        });
        return hasFailedDeposits;
    }

    async checkIfUserIsAbleToPayForItemsLocal(
        itemsIds: Array<string | number>,
        paymentType: PaymentTypeEnum
    ): Promise<any> {
        // TODO: refactor this logic. move it to viewModel or store
        const currentUser = this.firebase.auth.currentUser;
        const userId = currentUser && currentUser.uid;
        if (!userId) {
            return false;
        }
        if (paymentType === PaymentTypeEnum.DEPOSIT_OFFER) {
            return;
        }
        if (paymentType === PaymentTypeEnum.PAY_IN_FULL) {
            const hasFailedDeposits = await this.checkHasFailedDeposits(userId, itemsIds);
            if (hasFailedDeposits) {
                throw new Error(
                    "Unable to pay for vehicles. Please make sure you've payed for the outstanding deposit."
                );
            }
        }
    }

    async generateCheckout(itemsIds: string[], paymentType: PaymentTypeEnum): Promise<any> {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_GENERATE_CHECKOUT)({
            itemsIds,
            paymentType,
        });
    }

    subscribeToCcHolds(uid: string, listener: (holds: {[key: string]: PaymentIntentHoldDto}) => void) {
        return this.firebase.firestore.subscribeToCollection<any>(
            this.firebase.firestore.collectionRef(`/users/${uid}/holds`),
            (result) => listener(result || {})
        );
    }

    subscribeToCcHoldForAuction(
        {uid, regionId, auctionId}: {uid: string; regionId: string; auctionId: string},
        subscriber: (hold: PaymentIntentHoldDto | undefined) => void
    ) {
        return this.firebase.firestore.subscribeToDocument<PaymentIntentHoldDto>(
            this.firebase.firestore.documentRef(`/users/${uid}/holds/${regionId}-auctions-${auctionId}`),
            (hold: any) => {
                subscriber(hold);
            }
        );
    }

    subscribeToCcHoldPerVehicle(
        {uid, regionId, auctionId, itemId}: {uid: string; regionId: string; auctionId: string; itemId: string},
        subscriber: (hold: PaymentIntentHoldDto | undefined) => void
    ) {
        return this.firebase.firestore.subscribeToDocument<PaymentIntentHoldDto>(
            this.firebase.firestore.documentRef(
                `/users/${uid}/holds/${regionId}-auctions-${auctionId}/items/${itemId}`
            ),
            (hold: any) => {
                subscriber(hold);
            }
        );
    }

    async fetchCcHoldForAuction({
        uid,
        regionId,
        auctionId,
    }: {
        uid: string;
        regionId: string;
        auctionId: string;
    }): Promise<PaymentIntentHoldDto | undefined> {
        const hold = await this.firebase.firestore.fetchOnce<PaymentIntentHoldDto>(
            this.firebase.firestore.documentRef(`/users/${uid}/holds/${regionId}-auctions-${auctionId}`)
        );
        return hold ?? undefined;
    }

    async confirmCcHold({regionId, auctionId, itemId}: {regionId: string; auctionId: string; itemId: string}) {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_CONFIRM_CC_HOLD)({
            regionId,
            auctionId,
            itemId,
        });
    }

    async confirmCcHoldAndPlaceCcHoldForBidder(
        data: ConfirmCcHoldAndPlaceCcHoldForBidderRPCReqData
    ): Promise<ConfirmCcHoldAndPlaceCcHoldForBidderRPCResData> {
        const {regionId, auctionId, itemId} = data;
        const userId = this.firebase.auth.currentUser?.uid;

        this.logger.log('data', data);

        const result = await this.firebase.rpcService.call(
            FeReqRouteEnum.API_PAYMENT_CONFIRM_CC_HOLD_AND_PLACE_CC_HOLD_FOR_BIDDER
        )<ConfirmCcHoldAndPlaceCcHoldForBidderRPCReqData, ConfirmCcHoldAndPlaceCcHoldForBidderRPCResData>(data);

        if (data.holdScope === CCHoldScopeEnum.PER_VEHICLE) {
            await promiseWithTimeout(
                CONFIRM_CC_HOLD_PER_VEHICLE_TIMEOUT_IN_MS,
                () =>
                    new Promise<WithKey<PaymentIntentHoldDto> | undefined>((resolve, reject) => {
                        this.firebase.firestore.subscribeToDocument<WithKey<PaymentIntentHoldDto>>(
                            this.firebase.firestore.documentRef(
                                `/users/${userId}/holds/${regionId}-auctions-${auctionId}/items/${itemId}`
                            ),
                            (hold, error) => {
                                this.logger.log({hold, error});
                                if (error) {
                                    reject(error);
                                } else {
                                    resolve(hold);
                                }
                            }
                        );
                    }),
                'check cc hold per vehicle confirmation timeout'
            );
        } else if (data.holdScope === CCHoldScopeEnum.PER_AUCTION) {
            await promiseWithTimeout(
                CONFIRM_CC_HOLD_PER_AUCTION_TIMEOUT_IN_MS,
                () =>
                    new Promise<WithKey<PaymentIntentHoldDto> | undefined>((resolve, reject) => {
                        this.firebase.firestore.subscribeToDocument<WithKey<PaymentIntentHoldDto>>(
                            this.firebase.firestore.documentRef(
                                `/users/${userId}/holds/${regionId}-auctions-${auctionId}`
                            ),
                            (hold, error) => {
                                this.logger.log({hold, error});
                                if (error) {
                                    reject(error);
                                } else {
                                    resolve(hold);
                                }
                            }
                        );
                    }),
                'check cc hold per vehicle confirmation timeout'
            );
        }
        return result;
    }

    async placeCcHoldForBidder(data: {regionId: string; auctionId: string}): Promise<any> {
        return await this.firebase.rpcService.call(FeReqRouteEnum.API_PAYMENT_PLACE_CC_HOLD_FOR_BIDDER)(data);
    }
}
